import { useState } from 'react'
import type { Trophy } from '../lib/api/profile'
import { useT } from '../i18n'

interface Props {
  trophies: Trophy[]
  /** Tap a trophy to open the week it was won in. Omit on surfaces
   *  with nowhere to navigate (the shelf then renders inert). */
  onOpenTrophy?: (trophy: Trophy) => void
}

/**
 * Slug → URL map for trophy art. The slug is persisted on the
 * weekly_event row at finalize time (see migration 20260427160000)
 * and never changes after that — so a trophy stays bound to the
 * art that was randomly picked the moment voting closed. Tied
 * winners on the same event share the slug because they share the
 * event row.
 *
 * Adding new art only means dropping a webp into /public/trophies/,
 * registering it here, and adding the slug to `pick_trophy_art_slug`
 * in SQL. Existing trophies are untouched because their slug is
 * already pinned.
 */
const TROPHY_ART_URL: Record<string, string> = {
  'iet-classic':         '/trophies/iet-classic.webp',
  'iet-food':            '/trophies/iet-food.webp',
  'iet-tech':            '/trophies/iet-tech.webp',
  'iet-japan':           '/trophies/iet-japan.webp',
  'iet-africa':          '/trophies/iet-africa.webp',
  'iet-cheese':          '/trophies/iet-cheese.webp',
  'iet-forest':          '/trophies/iet-forest.webp',
  'iet-france':          '/trophies/iet-france.webp',
  'iet-jap2':            '/trophies/iet-jap2.webp',
  'iet-potman':          '/trophies/iet-potman.webp',
  'colorful-classic':    '/trophies/colorful-classic.webp',
  'colorful-food':       '/trophies/colorful-food.webp',
  'colorful-everyday':   '/trophies/colorful-everyday.webp',
  'colorful-animals':    '/trophies/colorful-animals.webp',
  'colorful-80':         '/trophies/colorful-80.webp',
  'colorful-banana':     '/trophies/colorful-banana.webp',
  'colorful-earth':      '/trophies/colorful-earth.webp',
  'colorful-house':      '/trophies/colorful-house.webp',
  'colorful-lemur':      '/trophies/colorful-lemur.webp',
  'colorful-painer':     '/trophies/colorful-painer.webp',
  'three-step-pets':     '/trophies/three-step-pets.webp',
  'three-step-pickle':   '/trophies/three-step-pickle.webp',
  'three-step-milk':     '/trophies/three-step-milk.webp',
  'three-step-eyes':     '/trophies/three-step-eyes.webp',
  'three-step-grandma':  '/trophies/three-step-grandma.webp',
  'three-step-bread':    '/trophies/three-step-bread.webp',
  'three-step-lamp':     '/trophies/three-step-lamp.webp',
  'three-step-mafia':    '/trophies/three-step-mafia.webp',
  'three-step-octopus':  '/trophies/three-step-octopus.webp',
  'three-step-walrus':   '/trophies/three-step-walrus.webp',
  'fake-out-magnifiers':    '/trophies/fake-out-magnifiers.webp',
  'fake-out-detectives':    '/trophies/fake-out-detectives.webp',
  'fake-out-produce':       '/trophies/fake-out-produce.webp',
  'fake-out-band':          '/trophies/fake-out-band.webp',
  'fake-out-hats':          '/trophies/fake-out-hats.webp',
  'fake-out-objects':       '/trophies/fake-out-objects.webp',
  'fake-out-wordtrain':     '/trophies/fake-out-wordtrain.webp',
  'fake-out-kids':          '/trophies/fake-out-kids.webp',
  'close-enough-space':     '/trophies/close-enough-space.webp',
  'close-enough-japan':     '/trophies/close-enough-japan.webp',
  'close-enough-microbes':  '/trophies/close-enough-microbes.webp',
  'close-enough-offroad':   '/trophies/close-enough-offroad.webp',
  'close-enough-poodle':    '/trophies/close-enough-poodle.webp',
  'close-enough-keitruck':  '/trophies/close-enough-keitruck.webp',
  'close-enough-samurai':   '/trophies/close-enough-samurai.webp',
  'close-enough-alabama':   '/trophies/close-enough-alabama.webp',
}

/**
 * Per-game default art used when a trophy row has no slug (legacy
 * events that predate the column, or any future case where the
 * finalize path didn't run). Pick the "classic" art so we never
 * fall through to the CSS gold-cup placeholder for known games.
 */
const DEFAULT_ART_BY_GAME: Record<Trophy['game_id'], string | null> = {
  ideatthat:    '/trophies/iet-classic.webp',
  colorful:     '/trophies/colorful-classic.webp',
  three_step:   '/trophies/three-step-pets.webp',
  'tokyo-metro': null,   // no art yet — CSS fallback
  fake_out:     '/trophies/fake-out-magnifiers.webp',
  close_enough: '/trophies/close-enough-space.webp',
}

function trophyArtFor(trophy: Trophy): string | null {
  if (trophy.trophy_art_slug && TROPHY_ART_URL[trophy.trophy_art_slug]) {
    return TROPHY_ART_URL[trophy.trophy_art_slug]
  }
  return DEFAULT_ART_BY_GAME[trophy.game_id]
}

/**
 * Trophy shelf — 3 trophies per shelf row, newest week first so a
 * fresh win is the first thing the player sees. Every trophy renders;
 * there is no cap, the shelf just grows. Each medallion is stamped
 * with the week's DATE (the same date the results popup and archive
 * use), not an internal sequence number a player can't map to
 * anything.
 *
 * The trophy art is a per-game PNG dropped at /public/trophies/.
 * If the PNG is missing we render a CSS-only fallback (gold disc
 * + date badge) so the shelf still looks intentional while we're
 * waiting on art.
 */
export function TrophyShelf({ trophies, onOpenTrophy }: Props) {
  const t = useT()
  if (trophies.length === 0) {
    return (
      <div className="flex items-center justify-center py-8 text-brand-muted">
        <div className="text-center">
          <div className="text-4xl mb-2">🏆</div>
          <div className="text-sm">{t('No trophies yet. Win a weekly event to earn one.')}</div>
        </div>
      </div>
    )
  }

  // Newest win first. The RPC returns oldest-first (week_number
  // ordering); reversing here keeps every caller consistent.
  const ordered = [...trophies].sort((a, b) => (a.week_start < b.week_start ? 1 : -1))

  // Pad to a full row of 3 (or up to two rows minimum) so the
  // shelf doesn't look lopsided with one trophy on it.
  const ROW = 3
  const minSlots = Math.max(ROW, Math.ceil(ordered.length / ROW) * ROW)
  const slots: (Trophy | null)[] = []
  for (let i = 0; i < minSlots; i++) slots.push(ordered[i] ?? null)

  // Group into rows of 3.
  const rows: (Trophy | null)[][] = []
  for (let i = 0; i < slots.length; i += ROW) rows.push(slots.slice(i, i + ROW))

  return (
    <div className="space-y-4">
      {rows.map((row, rIdx) => (
        <Shelf key={rIdx}>
          {row.map((trophy, i) => (
            <div key={i} className="flex-1 flex justify-center">
              {trophy
                ? <TrophyArt trophy={trophy} onOpen={onOpenTrophy} />
                : <EmptySlot />}
            </div>
          ))}
        </Shelf>
      ))}
    </div>
  )
}

function Shelf({ children }: { children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-end gap-3 px-2">{children}</div>
      {/* Wooden plank — gradient + dark underside for depth. Pure CSS,
          no extra image. */}
      <div className="relative h-3 mt-1 mx-1 rounded-xs
                      bg-linear-to-b from-[#A87B4A] via-[#8B5A2B] to-[#5C3A1B]
                      shadow-[0_4px_8px_rgba(0,0,0,0.18)]" />
    </div>
  )
}

/** "JUL 2026" — the trophy week's month + year. UTC because week keys
 *  are; the tooltip keeps the exact Monday for same-month wins. */
function weekBadge(weekStart: string): string {
  const d = new Date(weekStart + 'T00:00:00Z')
  const month = d
    .toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' })
    .toUpperCase()
  return `${month} ${d.getUTCFullYear()}`
}

function TrophyArt({
  trophy, onOpen,
}: {
  trophy: Trophy
  onOpen?: (t: Trophy) => void
}) {
  const t = useT()
  const [imgFailed, setImgFailed] = useState(false)
  const art = trophyArtFor(trophy)
  const showImage = art && !imgFailed
  const votes = trophy.total_votes === 1
    ? t('1 vote')
    : t('{n} votes', { n: trophy.total_votes })
  const tooltip = t('Week of {date} · {title} · {votes}', {
    date: trophy.week_start, title: trophy.dish_title, votes,
  })

  // A tappable trophy opens the week it was won in. Rendered as a real
  // button only when there's somewhere to go, so read-only surfaces
  // don't advertise a tap that does nothing.
  const Wrapper = onOpen ? 'button' : 'div'

  return (
    <Wrapper
      {...(onOpen
        ? {
            type: 'button' as const,
            onClick: () => onOpen(trophy),
            'aria-label': t('Open the week of {date}', { date: trophy.week_start }),
          }
        : {})}
      className={`flex flex-col items-center ${
        onOpen ? 'active:scale-95 transition-transform cursor-pointer' : ''
      }`}
      title={trophy.earned_stamp ? t('{tooltip} · earned a stamp', { tooltip }) : tooltip}
    >
      <div className="relative w-20 h-24">
        {/* Sole wins earn a stamp on the website; mark them with the
            stamp behind the trophy. Sits under the art on purpose —
            it should read as a backdrop, not compete with it. */}
        {trophy.earned_stamp && (
          <img
            src="/brand/stamp.svg"
            alt=""
            aria-hidden="true"
            draggable={false}
            className="absolute left-1/2 top-1/2 w-18 h-18 -translate-x-1/2 -translate-y-1/2
                       opacity-45 select-none pointer-events-none"
          />
        )}
        {showImage ? (
          <img
            src={art}
            alt={t('Trophy for the week of {date}', { date: trophy.week_start })}
            onError={() => setImgFailed(true)}
            draggable={false}
            className="absolute inset-0 w-full h-full object-contain select-none pointer-events-none
                       drop-shadow-[0_4px_6px_rgba(0,0,0,0.25)]"
          />
        ) : (
          <FallbackTrophy />
        )}
      </div>
      {/* Month-year plaque. Rendered in normal flow inside the same
          flex column as the trophy so it stays centered under it at
          every width, then pulled down with a negative margin so it
          sits ON the shelf plank instead of covering the trophy. */}
      <span className="relative z-10 -mb-4 mt-0.5 font-display font-black text-white text-[10px]
                       whitespace-nowrap bg-game-iet-dark/95 border border-white/40
                       rounded-full px-2 py-0.5 shadow-xs leading-none">
        {weekBadge(trophy.week_start)}
      </span>
    </Wrapper>
  )
}

/**
 * CSS-only fallback that runs when no /trophies/{game}-week.png file
 * has been added yet. Keeps the shelf looking intentional.
 */
function FallbackTrophy() {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-end">
      {/* Cup */}
      <div className="w-12 h-14 rounded-t-full
                      bg-linear-to-b from-[#F5D573] via-[#D4A030] to-[#8C6A1F]
                      border-2 border-[#A07820]
                      shadow-[inset_0_-4px_6px_rgba(0,0,0,0.25),inset_0_4px_6px_rgba(255,255,255,0.4)]" />
      {/* Stem */}
      <div className="w-3 h-2 bg-[#8C6A1F]" />
      {/* Plinth */}
      <div className="w-14 h-3 rounded-xs
                      bg-linear-to-b from-[#D4A030] to-[#8C6A1F]
                      border border-[#7A5A18]" />
    </div>
  )
}

function EmptySlot() {
  return (
    <div className="w-20 h-24 flex flex-col items-center justify-end opacity-40">
      <div className="w-12 h-14 rounded-t-full border-2 border-dashed border-brand-border" />
      <div className="w-14 h-3 rounded-xs border border-dashed border-brand-border mt-2" />
    </div>
  )
}
